import { useCallback, useEffect, useState } from 'react';
import { MediaClient } from '@tier4/webauto-media-client';
import { TelephoneState } from 'components/views/molecules/Cockpit/TelephoneButton';
import { useAuthTokenRepository } from 'services/useAuthTokenRepository';
import { CustomAudioDeviceManager } from './CustomAudioDeviceManager';
import { useRingingDevices } from './useRingingDevices';

export function useOperatorCallRemoteDevices(vehicleId: string, projectId: string, environmentId: string) {
  const [telephones, setTelephones] = useState<Record<string, TelephoneState>>({});
  const [devices, setDevices] = useState<MediaClient.RemoteDevices>();
  const authTokenRepos = useAuthTokenRepository();
  const { getRingingDevice } = useRingingDevices();

  useEffect(() => {
    const getter = async () => ({ token: await authTokenRepos.get(), expiredAt: new Date() });
    MediaClient.addAuthTokenCallback(getter);
    getter().then(async () => {
      const devices = await MediaClient.getRemoteDevices({
        vehicleId,
        projectId,
        environmentId,
        audioDeviceManager: new CustomAudioDeviceManager()
      });
      setDevices(devices);
    });
    return () => MediaClient.removeAuthTokenCallback(getter);
  }, [authTokenRepos, environmentId, projectId, vehicleId]);

  useEffect(() => {
    const handleUpdateTelephoneState = (tag: string, status: TelephoneState) => {
      setTelephones((prevState) => ({
        ...prevState,
        [tag]: status,
      })
      );
    };

    if (devices?.telephones?.length) {
      setTelephones(Object.fromEntries(devices.telephones.map(t => [t.tag, 'disabled'])));

      devices.telephones.forEach(async (telephone) => {
        const ringingDevice = getRingingDevice(telephone.tag);

        telephone.addStateListener(async () => {
          switch (telephone.state) {
            case 'offline':
              handleUpdateTelephoneState(telephone.tag, 'disabled');
              ringingDevice.stop();
              break;
            case 'online':
              handleUpdateTelephoneState(telephone.tag, 'ready');
              ringingDevice.stop();
              break;
            case 'ringing':
              ringingDevice.ringing();
              handleUpdateTelephoneState(telephone.tag, 'ringing');
              break;
            case 'calling':
              ringingDevice.ringing();
              handleUpdateTelephoneState(telephone.tag, 'calling');
              break;
            case 'talking':
              handleUpdateTelephoneState(telephone.tag, 'talking');
              ringingDevice.stop(true);
              break;
            case 'error':
              handleUpdateTelephoneState(telephone.tag, 'disabled');
              ringingDevice.stop();
              break;
          }
        });

        await telephone.connect();
      });
    }
  }, [devices, getRingingDevice]);

  const toggleCallsState = useCallback((tag: string) => {
    const currentPhone = devices?.telephones.find((phone) => phone.tag === tag);
    const isAnyOtherPhoneTalking = devices?.telephones.find((phone) => phone.state === 'talking' && phone.tag !== tag);

    if (!currentPhone) return;
    switch (telephones[tag]) {
      case 'ready':
        if (isAnyOtherPhoneTalking) return;
        currentPhone.call();
        break;
      case 'calling':
      case 'talking':
        currentPhone.hangup();
        break;
      case 'ringing':
        if (isAnyOtherPhoneTalking) return;
        currentPhone.answer();
        break;
    }
  }, [devices, telephones]);

  return {
    telephones,
    toggleCallsState
  };
}